import api from '@api';
import { ArtifactRenderer, catalog } from '@artifact-viewer';
import { Log } from '@enums';
import BackIcon from '@icons/back.svg?react';
import CloseIcon from '@icons/close.svg?react';
import type { Spec } from '@json-render/react';
import { useShellStore } from '@store/shell';
import { PrimaryButton } from '@ui/buttons';
import { ContentIcon } from '@ui/icons';
import * as utils from '@utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import Styled from './ArtifactPanel.styled';

function ArtifactPanel() {
  const artifactId = useShellStore(
    (store) => store.artifactViewerProps.artifactId
  );
  const title = useShellStore((store) => store.artifactViewerProps.title);
  const isOpen = useShellStore((store) => store.isArtifactViewerOpen);
  const setIsArtifactViewerOpen = useShellStore(
    (store) => store.setIsArtifactViewerOpen
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const artifactRef = useRef<HTMLDivElement>(null);
  const [artifactSpec, setArtifactSpec] = useState<Spec | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const setToast = useShellStore((store) => store.setToast);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const element = event.target as HTMLElement;

      if (element.scrollTop > 0) {
        headerRef.current?.classList.add('shadow');
      } else {
        headerRef.current?.classList.remove('shadow');
      }
    };

    contentRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      contentRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !artifactId) {
      setArtifactSpec(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    setArtifactSpec(null);
    setIsLoading(true);

    api.artifacts
      .getArtifact(artifactId)
      .then(({ content }) => {
        if (cancelled) {
          return;
        }

        const validated = catalog.validate(content);

        if (validated.success) {
          setArtifactSpec(validated.data as Spec);
        } else {
          utils.log('Spec validation failed.', {
            type: Log.Warning,
            id: artifactId,
            data: validated.error?.issues
          });
          setArtifactSpec(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setArtifactSpec(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [artifactId, isOpen]);

  const handleDownload = useCallback(async () => {
    if (!artifactRef.current || isDownloading) {
      return;
    }

    const filename = (title?.trim() || artifactId || 'artifact')
      .replace(/[:,']/g, '')
      .replace(/[^\w-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();

    setIsDownloading(true);

    try {
      await utils.downloadElementAsPng(artifactRef.current, filename, {
        padding: 32
      });
    } catch (error) {
      setToast({
        status: 'error',
        message: 'Failed to download artifact.'
      });
      console.error('Failed to download artifact:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [artifactId, isDownloading, setToast, title]);

  return (
    <Styled.Wrapper data-testid="artifact-panel" isOpen={isOpen}>
      <Styled.Header ref={headerRef}>
        <Styled.Title>Artifact</Styled.Title>
        <Styled.CloseButton onClick={() => setIsArtifactViewerOpen(false)}>
          <BackIcon className="back icon" />
          <CloseIcon className="close icon" />
        </Styled.CloseButton>
      </Styled.Header>
      <Styled.Content className="overlay-scrollbar" ref={contentRef}>
        {isLoading ? (
          <Styled.Description>Loading Artifact...</Styled.Description>
        ) : artifactSpec ? (
          <>
            <Styled.ArtifactToolbar>
              {title ? (
                <Styled.ArtifactTitle>{title}</Styled.ArtifactTitle>
              ) : null}
              <PrimaryButton
                data-testid="artifact-download"
                disabled={isDownloading}
                startIcon={<ContentIcon />}
                type="button"
                onClick={handleDownload}>
                {isDownloading ? 'Downloading...' : 'Download'}
              </PrimaryButton>
            </Styled.ArtifactToolbar>
            <Styled.ArtifactCapture ref={artifactRef}>
              <ArtifactRenderer spec={artifactSpec} />
            </Styled.ArtifactCapture>
          </>
        ) : (
          <Styled.Description>Artifact unavailable.</Styled.Description>
        )}
      </Styled.Content>
    </Styled.Wrapper>
  );
}

export default ArtifactPanel;
